/**
 * useOnboarding Hook
 * Custom hook for managing client onboarding
 */

import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import onboardingService from '../services/onboardingService';
import { apiRequest } from '../config/authConfig';

/**
 * Hook for creating onboarding links (admin side)
 * @returns {Object} Onboarding link creation functions and state
 */
export const useOnboarding = () => {
    const { instance, accounts } = useMsal();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getAccessToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) {
            throw new Error('No active account. Please sign in.');
        }

        try {
            const response = await instance.acquireTokenSilent({
                ...apiRequest,
                account: accounts[0]
            });
            return response.accessToken;
        } catch (error) {
            console.error('Failed to acquire token:', error);
            throw error;
        }
    }, [instance, accounts]);

    const createOnboardingLink = useCallback(async (request) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessToken();
            const response = await onboardingService.createOnboardingLink(token, request);
            return response;
        } catch (err) {
            console.error('Failed to create onboarding link:', err);
            setError(err.message || 'Failed to create onboarding link');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    const invalidateTokens = useCallback(async (email) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessToken();
            const response = await onboardingService.invalidateTokens(token, { email });
            return response;
        } catch (err) {
            console.error('Failed to invalidate tokens:', err);
            setError(err.message || 'Failed to invalidate tokens');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    return {
        loading,
        error,
        createOnboardingLink,
        invalidateTokens
    };
};

/**
 * Hook for managing onboarding form (client side - public)
 * @param {string} token - Onboarding token
 * @returns {Object} Form state and submission functions
 */
export const useOnboardingForm = (token) => {
    const [formState, setFormState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const loadForm = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await onboardingService.getOnboardingForm(token);
            setFormState(data);
        } catch (err) {
            console.error('Failed to load onboarding form:', err);
            setError(err.message || 'Failed to load form');
        } finally {
            setLoading(false);
        }
    }, [token]);

    const submitOnboarding = useCallback(async (data) => {
        if (!token) {
            throw new Error('No token provided');
        }

        try {
            setSubmitting(true);
            setError(null);
            const result = await onboardingService.submitOnboarding(token, data);
            return result;
        } catch (err) {
            console.error('Failed to submit onboarding:', err);
            setError(err.message || 'Failed to submit registration');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [token]);

    return {
        formState,
        loading,
        error,
        submitting,
        loadForm,
        submitOnboarding
    };
};

/**
 * Hook for managing onboarding requests (admin side)
 * @returns {Object} Onboarding requests functions and state
 */
export const useOnboardingRequests = () => {
    const { instance, accounts } = useMsal();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getAccessToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) {
            throw new Error('No active account. Please sign in.');
        }

        try {
            const response = await instance.acquireTokenSilent({
                ...apiRequest,
                account: accounts[0]
            });
            return response.accessToken;
        } catch (error) {
            console.error('Failed to acquire token:', error);
            throw error;
        }
    }, [instance, accounts]);

    const getRequests = useCallback(async (filters = {}) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessToken();
            const response = await onboardingService.getOnboardingRequests(token, filters);
            return response;
        } catch (err) {
            console.error('Failed to get onboarding requests:', err);
            setError(err.message || 'Failed to get onboarding requests');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    const getRequestDetails = useCallback(async (tokenId) => {
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessToken();
            const response = await onboardingService.getOnboardingRequestDetails(token, tokenId);
            return response;
        } catch (err) {
            console.error('Failed to get request details:', err);
            setError(err.message || 'Failed to get request details');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    return {
        loading,
        error,
        getRequests,
        getRequestDetails
    };
};

export default useOnboarding;
